/* eslint-disable consistent-return */
import './Document.less';
import { useGetDocumentQuery } from '@redux/api/adminApi';
import { Loader } from '@sberlab/core-components';
import { IDocumentDTO } from '@typings/rootSlice';
import parse, { Element } from 'html-react-parser';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';

interface IProps {
    id: number;
    documentTree: Record<string, IDocumentDTO>;
}

const Document: React.FC<IProps> = ({ id, documentTree }) => {
    const navigate = useNavigate();
    const { namespace } = useParams();
    const { data, isFetching: isDataLoading } = useGetDocumentQuery(id, {
        skip: !id,
    });

    // Вешаем события на контент редактора (врезы)
    useEffect(() => {
        if (!data) return;

        const tabsEl = document.querySelectorAll('.editor-tabs__head > div');
        const copyIcon = document.querySelectorAll('.editor-code-copy');
        const links = document.querySelectorAll('#editor-subpages-menu a');

        const clickTabHandler = (e) => {
            if (e.target.classList.contains('active')) return;

            const index = e.target.dataset.id;
            const container = e.target.parentElement.parentElement;
            const contentTabs = container.querySelectorAll('.editor-tabs__content > div');
            const contentTab = container.querySelector(`.editor-tabs__content > div:nth-child(${index})`);
            const headerTabs = container.querySelectorAll('.editor-tabs__head > div');
            headerTabs.forEach((el) => el.classList.remove('active'));
            e.target.classList.add('active');
            contentTabs.forEach((el) => el.classList.remove('active'));
            contentTab.classList.add('active');
        };

        const clickCopyCodeHandler = (e) => {
            const code = e.target.parentElement.querySelector('pre').textContent;

            if (navigator.clipboard) {
                navigator.clipboard.writeText(code);
            } else {
                const tempInput = document.createElement('input');
                tempInput.value = code;
                document.body.appendChild(tempInput);
                tempInput.select();
                document.execCommand('copy');
                document.body.removeChild(tempInput);
            }
        };

        const clickLinkHandler = (e) => {
            e.preventDefault();
            navigate(e.target.getAttribute('href'));
        };

        tabsEl.forEach((tab) => tab.addEventListener('click', clickTabHandler));
        copyIcon.forEach((icon) => icon.addEventListener('click', clickCopyCodeHandler));
        links.forEach((link) => link.addEventListener('click', clickLinkHandler));


        // eslint-disable-next-line consistent-return
        return () => {
            tabsEl.forEach((tab) => tab.removeEventListener('click', clickTabHandler));
            copyIcon.forEach((icon) => icon.removeEventListener('click', clickCopyCodeHandler));
            links.forEach((link) => link.removeEventListener('click', clickLinkHandler));
        };
    }, [data]);

    if (!data) return null;

    // Заменяем вставку меню подразделов на сгенерированные ссылки
    const subpages = data?.children;
    const subpagesMenu = subpages.reduce(
        (html, item, i) =>
            `${html}<div><a href="/${namespace}/${item}">&#8226; ${documentTree?.[item]?.title}</a></div>${
                i === subpages.length - 1 ? '<' : ''
            }`,
        '>',
    );
    const body = data?.body.replace(/>Меню подразделов</gi, subpagesMenu);

    const articleContent = parse(body, {
        replace(node) {
            if (!(node instanceof Element)) return;

            if (node?.attribs?.class === 'custom_picture custom_item') {
                const image = data.files?.find((item) => String(item.id) === node?.attribs?.id);

                if (!image) return <span />;

                return <div className="document__image">
                    <img src={image.filename} alt={image.description} />
                    {(image.description || image.copyright) && (
                        <div className="document__file__info">
                            {image.description && <span className="document__file__descr">{parse(image.description)}</span>}
                            {image.copyright && <span className="document__file__copy">{parse(image.copyright)}</span>}
                        </div>
                    )}
                </div>;
            }

            if (node?.attribs?.class === 'custom_video custom_item') {
                if (node?.attribs?.id) {
                    const video = data.files?.find((item) => String(item.id) === node?.attribs?.id);

                    if (!video) return <span />;

                    return <div className="document__video">
                        <div className="document__video__container">
                            <div className="document__video__inner">
                                <iframe className="document__video__iframe" title={video.description} src={video.filename} width="720" height="405" frameBorder="0" allow="clipboard-write; autoplay" allowFullScreen>&nbsp;</iframe>
                            </div>
                        </div>
                        {(video.description || video.copyright) && (
                            <div className="document__file__info">
                                {video.description && <span className="document__file__copy">{parse(video.description)}</span>}
                                {video.copyright && <span className="document__file__copy">{parse(video.copyright)}</span>}
                            </div>
                        )}
                    </div>; 
                }
            }

            if (node?.attribs?.class === 'custom_file custom_item') {
                if (node?.attribs?.id) {
                    const file = data.files?.find((item) => String(item.id) === node?.attribs?.id);

                    if (!file) return <span />;

                    return <a href={file.filename} className="document__file" target="_blank" rel="noopener">{file.description || file.filename}</a>
                }
            }

            if (node.tagName === 'table' && node instanceof HTMLElement) {
                return <div className="editor-table">{parse(node.innerHTML)}</div>;
            }
        },
    });

    return (
        <div className="document">
            <Loader loading={isDataLoading}>
                <div className="document__inner">{articleContent}</div>
            </Loader>
        </div>
    );
};

export default Document;
